import { faArrowDown, faArrowLeft, faArrowRight, faArrowUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Input, Modal, Tabs } from "antd";
import React, { useContext, useState, useMemo, useCallback, useEffect } from "react";
import { Store } from "../../store";
import { SequenceGenerator } from "../../utils/SequenceGenerator";
import PatternBuilder from "../RackFrontView/PatternBuilder";
import { DIRECTION_CONFIG, EDITOR_MODES } from "./constants";

const GenerateBinLabelsForm = ({ showGenerateBinLabelsForm, setShowGenerateBinLabelsForm, selectedElements }) => {
  const { state, dispatch } = useContext(Store);
  const { elements } = state.rackEditor;
  const [patternEditorMode, setPatternEditorMode] = useState(EDITOR_MODES.BUILDER);
  const [sequencePattern, setSequencePattern] = useState("");
  const [previewLabels, setPreviewLabels] = useState(false);
  const [labelConfig, setLabelConfig] = useState({
    HORIZONTAL_DIRECTION: DIRECTION_CONFIG.HORIZONTAL.LTR,
    VERTICAL_DIRECTION: DIRECTION_CONFIG.VERTICAL.BTT,
  });

  // Memoized selected elements array
  const selectedElementsArray = useMemo(() => Array.from(selectedElements), [selectedElements]);

  // Memoized filtered elements
  const filteredElements = useMemo(() => elements.filter((element) => selectedElements.has(element.id)), [elements, selectedElements]);

  // Close modal handler
  const closeModal = useCallback(() => {
    setSequencePattern("");
    setPreviewLabels(false);
    setShowGenerateBinLabelsForm(false);
  }, [setShowGenerateBinLabelsForm]);

  // Update label config handler
  const updateLabelConfig = useCallback((axis, position) => {    
    setLabelConfig((prevState) => ({
      ...prevState,
      [axis]: position,
    }));
  }, []);

  // Reorder levels by direction
  const reorderLevelsByDirection = useCallback(
    (levels) => {
      const { VERTICAL_DIRECTION } = labelConfig;
      return VERTICAL_DIRECTION === DIRECTION_CONFIG.VERTICAL.TTB ? levels.reverse() : levels;
    },
    [labelConfig]
  );

  // Reorder bins by direction
  const reorderBinsByDirection = useCallback(
    (bins) => {
      const { HORIZONTAL_DIRECTION } = labelConfig;
      return HORIZONTAL_DIRECTION === DIRECTION_CONFIG.HORIZONTAL.RTL ? bins.reverse() : bins;
    },
    [labelConfig]
  );

  // Generate labels handler
  const onGenerateBinLabels = useCallback(
    ({ pattern }) => {
      if (!pattern) {
        console.error("No pattern provided");
        return;
      }

      try {
        // Group elements by y-axis (levels)
        const levels = filteredElements.reduce((group, bin) => {
          const levelKey = bin.y1;
          if (!group[levelKey]) {
            group[levelKey] = [];
          }
          group[levelKey].push(bin);
          return group;
        }, {});

        // Sort bins in each level by x-axis
        Object.values(levels).forEach((levelBins) => {
          levelBins.sort((a, b) => a.x1 - b.x1);
        });

        const sortedLevels = Object.keys(levels).sort((a, b) => b - a);
        const directionSortedLevels = reorderLevelsByDirection(sortedLevels);
        const extendedSequenceGenerator = new SequenceGenerator(pattern, {
          overflowStrategy: "extend",
        });

        // Update each bin with generated code
        directionSortedLevels.forEach((level) => {
          const bins = levels[level];
          const directionSortedBins = reorderBinsByDirection(bins);

          directionSortedBins.forEach((bin) => {
            const metadata = bin.labels.reduce((acc, label) => {
              acc[label.type] = label.name;
              return acc;
            }, {});

            dispatch({
              type: "RE_UPDATE_ELEMENT",
              payload: {
                ...bin,
                code: extendedSequenceGenerator.generate(metadata),
              },
            });
          });
        });

        closeModal();
      } catch (error) {
        console.error("Error generating bin labels:", error);
        // You might want to show a toast notification here
      }
    },
    [filteredElements, reorderLevelsByDirection, reorderBinsByDirection, dispatch, closeModal]
  );

  // Render preview labels
  const renderPreviewLabels = useCallback(
    (pattern) => {
      if (!pattern) return null;

      try {
        const generator = new SequenceGenerator(pattern, {
          overflowStrategy: "extend",
        });

        const labels = filteredElements.map((element) => {
          const metadata = element.labels.reduce((acc, label) => {
            acc[label.type] = label.name;
            return acc;
          }, {});
          return generator.generate(metadata);
        });

        return (
          <div className="preview-labels">
            {labels.map((label, index) => (
              <div key={`${label}-${index}`} className="preview-label">
                <div className="preview-label-name">{label}</div>
              </div>
            ))}
          </div>
        );
      } catch (error) {
        console.error("Error rendering preview labels:", error);
        return <div>Error generating preview</div>;
      }
    },
    [filteredElements]
  );

  // Tab items configuration
  const getGenerateBinLabelsTabItems = useMemo(() => {
    const baseTabs = [
      {
        key: EDITOR_MODES.BUILDER,
        label: "Build Pattern",
        children: (
          <div style={{ marginBottom: "0.5rem" }}>
            <PatternBuilder key={EDITOR_MODES.BUILDER} onPatternChange={setSequencePattern} />
          </div>
        ),
      },
      {
        key: EDITOR_MODES.INPUT,
        label: "Type Pattern",
        children: (
          <div
            style={{
              display: "flex",
              gap: "10px",
              justifyContent: "center",
              flexDirection: "column",
              marginBottom: "0.5rem",
            }}
          >
            <label style={{ fontSize: "15px" }} htmlFor="pattern">
              Pattern:
            </label>
            <Input
              style={{ width: "100%" }}
              type="text"
              name="pattern"
              id="pattern"
              value={sequencePattern}
              onChange={(e) => setSequencePattern(e.target.value)}
              placeholder="Enter your pattern here..."
            />
          </div>
        ),
      },
    ];

    const existingTab = {
      key: EDITOR_MODES.EXISTING,
      label: "Edit Existing",
      children: (
        <div
          style={{
            display: "flex",
            gap: "10px",
            justifyContent: "center",
            flexDirection: "column",
            marginBottom: "0.5rem",
          }}
        >
          <label style={{ fontSize: "15px" }} htmlFor="pattern">
            Pattern:
          </label>
          <Input
            style={{ width: "100%" }}
            type="text"
            name="pattern"
            id="pattern"
            value={sequencePattern}
            onChange={(e) => setSequencePattern(e.target.value)}
            placeholder="Enter your pattern here..."
          />
        </div>
      ),
    };

    // Determine which tabs to show based on selection count
    const selectedCount = selectedElements.size;

    if (selectedCount > 1) {
      // return [...baseTabs, existingTab];
      return [...baseTabs];
    } else if (selectedCount === 1) {
      // return [existingTab];
      return []
    } else {
      return baseTabs;
    }
  }, [selectedElements.size, sequencePattern]);

  // Determine default active tab based on selection
  const defaultActiveTab = useMemo(() => (selectedElements.size === 1 ? EDITOR_MODES.EXISTING : EDITOR_MODES.BUILDER), [selectedElements.size]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (showGenerateBinLabelsForm) {
      setPatternEditorMode(defaultActiveTab);
    }
  }, [showGenerateBinLabelsForm, defaultActiveTab]);

  if (!showGenerateBinLabelsForm) return null;

  return (
    <Modal
      centered
      maskClosable={false}
      width={700}
      cancelText={"Cancel"}
      okText={"Apply"}
      title={"Generate Bin Labels"}
      open={true}
      onOk={() => onGenerateBinLabels({ pattern: sequencePattern })}
      onCancel={closeModal}
      okButtonProps={{ disabled: !sequencePattern }}
    >
      {/* Tabs for Pattern Editor */}
      <Tabs activeKey={patternEditorMode} onChange={setPatternEditorMode} items={getGenerateBinLabelsTabItems} />

      {/* Labels direction selection - Only show for multiple selections */}
      {selectedElements.size > 1 && (
        <>
            <DirectionConfiguration
                labelConfig={labelConfig}
                updateLabelConfig={updateLabelConfig}
                previewLabels={previewLabels}
                setPreviewLabels={setPreviewLabels}
                sequencePattern={sequencePattern}
            />
            {/* Preview Labels Modal */}
            <PreviewModal
                previewLabels={previewLabels}
                setPreviewLabels={setPreviewLabels}
                sequencePattern={sequencePattern}
                renderPreviewLabels={renderPreviewLabels}
            />
        </>
      )}

    </Modal>
  );
};

// Sub-component for direction configuration
const DirectionConfiguration = React.memo(({ labelConfig, updateLabelConfig, previewLabels, setPreviewLabels, sequencePattern }) => (
  <>
    <div className="orientation-container" style={{ marginTop: "20px" }}>
      <DirectionSection
        label="Horizontal Direction"
        axis="HORIZONTAL_DIRECTION"
        labelConfig={labelConfig}
        updateLabelConfig={updateLabelConfig}
        leftOption={{ value: DIRECTION_CONFIG.HORIZONTAL.LTR, icon: faArrowRight }}
        rightOption={{ value: DIRECTION_CONFIG.HORIZONTAL.RTL, icon: faArrowLeft }}
      />

      <DirectionSection
        label="Vertical Direction"
        axis="VERTICAL_DIRECTION"
        labelConfig={labelConfig}
        updateLabelConfig={updateLabelConfig}
        leftOption={{ value: DIRECTION_CONFIG.VERTICAL.BTT, icon: faArrowUp }}
        rightOption={{ value: DIRECTION_CONFIG.VERTICAL.TTB, icon: faArrowDown }}
      />
    </div>

    {
        sequencePattern && (
            <div style={{ margin: "20px 0px" }}>
            <p
                style={{
                color: "#3656fb",
                textDecoration: "underline",
                cursor: "pointer",
                margin: 0,
                }}
                onClick={() => setPreviewLabels(!previewLabels)}
            >
                Preview Labels
            </p>
            </div>
        )
    }
  </>
));

// Sub-component for direction section
const DirectionSection = React.memo(({ label, axis, labelConfig, updateLabelConfig, leftOption, rightOption }) => (
  <div className="label-container">
    <label style={{ fontSize: "1rem", marginRight: "1rem" }}>{label}</label>
    <div className="label-wrapper">
      <DirectionButton isActive={labelConfig[axis] === leftOption.value} onClick={() => updateLabelConfig(axis, leftOption.value)} icon={leftOption.icon} />
      <DirectionButton isActive={labelConfig[axis] === rightOption.value} onClick={() => updateLabelConfig(axis, rightOption.value)} icon={rightOption.icon} />
    </div>
  </div>
));

// Sub-component for direction button
const DirectionButton = React.memo(({ isActive, onClick, icon }) => (
  <div className={`direction-label ${isActive ? "active" : ""}`} onClick={onClick}>
    <FontAwesomeIcon icon={icon} />
  </div>
));

// Sub-component for preview modal
const PreviewModal = React.memo(({ previewLabels, setPreviewLabels, sequencePattern, renderPreviewLabels }) => (
  <Modal
    centered
    maskClosable={false}
    width={400}
    open={previewLabels && !!sequencePattern}
    footer={null}
    onCancel={() => setPreviewLabels(false)}
    title="Preview Labels"
  >
    {renderPreviewLabels(sequencePattern)}
  </Modal>
));

export default GenerateBinLabelsForm;
